import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import React, {useContext, useEffect, useState} from "react";
import { makeStyles } from "@material-ui/core/styles";
import Colors from "../../utils/colors";
import apiFetch, {getData} from '../../utils/apiUtils';
import { MilestoneTypes } from "../../utils/constants";
import {ErrorContext} from "../../utils/constants";
import EnhancedTextInput from "../EnhancedTextInput";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';

import moment from "moment";
import MomentUtils from "@date-io/moment";

const useStyles = makeStyles(() => ({
    milestoneHelp: {
        width: '33%',
        background: 'rgba(110, 247, 138, 0.1)',
        padding: '1rem'
    },
    milestoneEditorContainer: {
        backgroundColor: Colors.lightBlue,
        width: '63%'
    },
    editorTextField: {
        width: '95%',
        marginLeft: '1rem',
        backgroundColor: '#FFFFFF'
    },
    labelStyles: {
        width: '95%',
        marginLeft: '1rem',
        marginTop: '1rem',
    },
    'inline': {
        width: '45%',
        marginLeft: '0',
        "&:hover": {
          backgroundColor: Colors.brightBlue,
          color: Colors.indigoBlue
        },
    },
    error: {
      color: 'red',
      fontSize: '20px',
      paddingTop: '1rem',
      marginLeft: '1rem',
    },
    inputStyle: {
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: Colors.primary,
      transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      borderRadius: '4px',
      width: '95%',
      marginLeft: '1rem',
      backgroundColor: '#FFFFFF',
      '&:focus': {
        borderColor: Colors.tertiary,
        borderWidth: '3px',
        outline: '-webkit-focus-ring-color auto 0px'
      },
      '&:disabled': {
        backgroundColor: 'LightGrey'
      },
    },
    editorDateField: {
      width: '95%',
      backgroundColor: '#FFFFFF'
    },
    rowDiv: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      margin: '1rem',
    },
    editorDiv: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: '2rem',
    },
    deleteButton: {
        margin: '1rem 0',
        backgroundColor: Colors.orange,
        "&:hover": {
          backgroundColor: Colors.orangeRed,
          color: 'white'
        },
    },
    datepicker: {
      backgroundColor: 'white'
    }
}));
// Don't allow the user to select Stakeholder Review or Embed Submission steps
let milestoneTypes = MilestoneTypes.filter(type => type.value === 0 || type.value === 5);



const dateFormat = 'YYYY-MM-DD';

const formatDate = (date) => {
  if (date) {
    let newDate = date;
    if (newDate.indexOf('T') !== -1) {
      newDate = newDate.substring(0, newDate.indexOf('T'));
    }
    return moment(newDate).format(dateFormat);
  }
  return '';
};

const getOwner = (milestone) => {
  if (!milestone.owner) {
    return '';
  }
  return {
    displayName: milestone.owner,
    id: milestone.owner_oid,
    oid: milestone.owner_oid
  };
};

const calculateMinimumEndDate = (startDate) => {
  const minimumEndDate = startDate ? moment(startDate).add(1, 'days') : moment();
  return minimumEndDate.toString();
};

export default function MilestoneEditor({ milestone = { type: milestoneTypes[0].value },
                                          initiativeId,
                                          onClose,
                                          disabled,
                                          milestones,
                                          onSuccess,
                                          typeDisabled}) {
    const classes = useStyles();
    const { triggerError } = useContext(ErrorContext);

    const [ name, setName ] = useState(milestone.name ? milestone.name : '');
    const [ owner, setOwner ] = useState(getOwner(milestone));
    const [ description, setDescription ] = useState(milestone.description);
    const [ type, setType ] = useState(milestone.type);
    const [ startDate, setStartDate ] =
      useState(milestone.startDate? formatDate(milestone.startDate) : null);
    const [ endDate, setEndDate ] =
       useState(milestone.endDate?formatDate(milestone.endDate): null);
    const [ validate, setValidate ] = useState(false);
    const [ userAutocomplete, setUserAutocomplete ] = useState([]);
    const [ submitInProgress, setSubmitInProgress ] = useState(false);
    const [ minEndDate, setMinEndDate ] = useState(calculateMinimumEndDate(milestone.endDate, milestone.startDate));
    const [ showMissingFieldsMessage, setShowMissingFieldsMessage ] = useState(false);
    const [ showInvalidDateFormatMessage, setShowInvalidDateFormatMessage ] = useState(false);
    const [ showDatesIncorrectMessage, setShowDatesIncorrectMessage ] = useState(false);

    useEffect(() => {
      setName(milestone.name ? milestone.name : '');
      setOwner(getOwner(milestone));
      setDescription(milestone.description);
      setType(milestone.type);
      setStartDate(milestone.startDate? formatDate(milestone.startDate) : null);
      setEndDate(milestone.endDate?formatDate(milestone.endDate): null);
      setMinEndDate(calculateMinimumEndDate(milestone.endDate, milestone.startDate))
    }, [milestone]);

    const handleChangeStartDate = (date) => {
      setStartDate(date);
      setMinEndDate(calculateMinimumEndDate(date));
    };

    const handleChangeOld = (method) => event => {
        method(event.target.value);
    };
    
    const handleChange = (method) => value => {
      setSubmitInProgress(false);
      method(value);
    };

    const handleSubmit = () => {
        setSubmitInProgress(true);
        setShowMissingFieldsMessage(false);
        setShowDatesIncorrectMessage(false);
        setShowInvalidDateFormatMessage(false);
        if (!name || !owner || !description) {
          setValidate(true);
          setShowMissingFieldsMessage(true);
          setSubmitInProgress(false);
          return;
        }

        if (!moment(startDate, dateFormat, true).isValid() ||
            !moment(endDate, dateFormat, true).isValid()) {
          setSubmitInProgress(false);
          setShowInvalidDateFormatMessage(true);
          return;
        }

        if (endDate < startDate) {
          setSubmitInProgress(false);
          setShowDatesIncorrectMessage(true);
          return;
        }

        const milestoneData = {
            name, owner, description, type, startDate, endDate, id: milestone.id, status: 1
        };

        const onSubmitError = () => {
          setSubmitInProgress(false );
          triggerError({
            title: 'Communication Error',
            description: 'We were unable to save your milestone. Please try again.',
            onRetry: submit,
          });
        }

        const method = milestone.id? 'PATCH' : 'POST';

        const submit = () => apiFetch(`/initiatives/${initiativeId}/milestones`, method, JSON.stringify(milestoneData), data => {
            setSubmitInProgress(false);
            if (data) { // id is returned on a successful POST
                if (onSuccess) { onSuccess(); }
                onClose();
            } else { // This callback is still called even if the API call fails
                // TODO: Show error details
            }
        }, onSubmitError);
        submit();
    };

    const onAutocompleteError = (error) => {
      // deliberately left blank.  We don't want a popup with every letter typed.
      console.log(error);
    };

    const handleUserChange = (event) => {
      setSubmitInProgress(false );
      const value = event.target.value;
      if (value.length >=2) {
        getData('/users/' + value, setUserAutocomplete, onAutocompleteError);
      }
    };

    const handleUserSelect = (event, value) => {
      if(value !== null){
        value.oid = value.id;
        setOwner(value);
      }
    };

    const deleteMilestone = async () => {
      return apiFetch(
          `/initiatives/${initiativeId}/milestones/${milestone.id}`,
          'DELETE',
          null,
          onSuccess,
          () => triggerError({
              title: 'Communication Error',
              description: 'We were unable to delete your milestone. Please try again.',
              onRetry: deleteMilestone
          })
      );
  }

    return (
        <div className={classes.editorDiv}>
            <div className={classes.milestoneEditorContainer}>
               { showMissingFieldsMessage && <div className={classes.error} id="error-div">You must fill in all the fields!</div> }
               { showDatesIncorrectMessage && <div className={classes.error} id="error-div-dates">The end date must be after the start date!</div> }
               { showInvalidDateFormatMessage && <div className={classes.error} id="error-div-dates-format">Start and end dates must be in yyyy-mm-dd format!</div> }
               <div className={classes.labelStyles}>Milestone Name:</div>
                <EnhancedTextInput
                    maxText={255}
                    validate={validate}
                    changeMethod={handleChange(setName)}
                    placeholder="Milestone Name..."
                    initialValue={name}
                    autoFocus={true}
                    className={classes.editorTextField}
                    disabled={disabled}
                />
                <div className={classes.labelStyles}>Milestone Owner:</div>
                <Autocomplete
                  id={'milestone-user-autocomplete'}
                  options={userAutocomplete}
                  getOptionLabel={option => option? option.displayName : ''}
                  style={{ width: '100%' }}
                  value={owner}
                  size="small"
                  onChange={handleUserSelect}
                  disabled={disabled}
                  renderInput={params => (
                    <TextField {...params}
                               variant="outlined"
                               fullWidth
                               className={classes.inputStyle}
                               onChange={handleUserChange}
                               placeholder="User..."
                    />
                  )}
                />
                <div className={classes.labelStyles}>Description:</div>
                <EnhancedTextInput
                  maxText={2000}
                  validate={validate}
                  changeMethod={handleChange(setDescription)}
                  placeholder="Description..."
                  initialValue={description}
                  className={classes.editorTextField}
                  disabled={disabled}
                />
                <div className={classes.labelStyles}>Milestone Type:</div>
                <TextField
                    className={classes.inputStyle}
                    select
                    variant="outlined"
                    size="small"
                    onChange={handleChangeOld(setType)}
                    value={type}
                    disabled={typeDisabled || milestone.type === 5 }
                >
                    {milestoneTypes.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <div className={classes.rowDiv}>
                  <MuiPickersUtilsProvider utils={MomentUtils} style={{marginTop: '5rem'}}>
                    <KeyboardDatePicker
                      placeholder="YYYY/MM/DD"
                      value={startDate}
                      onChange={handleChangeStartDate}
                      format="YYYY/MM/DD"
                      inputVariant="outlined"
                      label="Start Date"
                      className={classes.datepicker}
                      disabled={disabled}
                    />
                    <KeyboardDatePicker
                      placeholder="YYYY/MM/DD"
                      value={endDate}
                      onChange={date => setEndDate(date)}
                      format="YYYY/MM/DD"
                      inputVariant="outlined"
                      label="End Date"
                      className={classes.datepicker}
                      disabled={disabled}
                      minDate={calculateMinimumEndDate(startDate)}
                    />
                  </MuiPickersUtilsProvider>
                </div>
                <div className={classes.rowDiv}>
                  <Button
                      className={classes.inline}
                      onClick={handleSubmit}
                      variant="contained"
                      color="primary"
                      disabled={submitInProgress}
                  >
                      SUBMIT
                  </Button>
                  <Button
                      className={classes.inline}
                      onClick={onClose}
                      variant="contained"
                      color="primary"
                  >
                      CANCEL
                  </Button>
              </div>
              {milestone.id && milestone.type !== 5 && milestone.type !== 6 && <div className={classes.rowDiv}>
                <Button
                    fullWidth
                    onClick={deleteMilestone}
                    variant='contained'
                    //color='secondary'
                    className={classes.deleteButton}
                >
                    DELETE MILESTONE
                </Button>
              </div>}
            </div>
            <div className={classes.milestoneHelp}>
              Indicate your Delivery Milestone using the Milestone Type dropdown. The Delivery Milestone marks the completion date of your initiative and is required to move to Confirm phase.
            </div>
        </div>
    );
};
